import { useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Checkbox } from '@finn-auto/design-system/atoms/checkbox';
import { Input } from '@finn-auto/design-system/atoms/input';
import { JoinError, submitSignup, type JoinLanguage } from '../../services/talentJoinApi';
import { JOIN_COPY, initialLanguage } from './joinCopy';
import { JoinShell } from './JoinShell';
import { useJoinConfig } from './useJoinConfig';

type FieldErrors = Partial<Record<'firstName' | 'lastName' | 'email' | 'linkedinUrl' | 'jobMatches', string>>;

/**
 * PUBLIC: the Talent Community sign-up, at /join/community.
 *
 * A recruiter pastes a link carrying the cluster and themselves (`?cluster=engineering&via=annika`)
 * into an InMail, so every sign-up arrives already tagged. The cluster is pre-selected from the
 * link and the candidate may change it; `via` is never shown.
 *
 * The consent boxes start UNTICKED (GDPR: consent is an action, never a default). Job matches is
 * required to join; FINN news is optional and separate.
 */
export default function TalentJoinView() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const [language, setLanguage] = useState<JoinLanguage>(() => initialLanguage(search, navigator.language));
  const config = useJoinConfig();
  const copy = JOIN_COPY[language];

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [cluster, setCluster] = useState(() => (params.get('cluster') || '').toLowerCase());
  const [jobMatches, setJobMatches] = useState(false);
  const [updates, setUpdates] = useState(false);
  const [website, setWebsite] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  // Two presses in one tick must send once; the Button's loading state does not disable it.
  const sending = useRef(false);

  const texts = config.data?.texts[language];
  const clusters = config.data?.clusters || [];
  const clusterKnown = clusters.some((c) => c.id === cluster);

  const errorText = (code: string | undefined, field?: keyof FieldErrors) => {
    if (!code) return undefined;
    if (field === 'jobMatches') return copy.errors.jobMatches;
    return code === 'required' ? copy.errors.required : copy.errors.invalid;
  };

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (sending.current) return;
    const local: FieldErrors = {};
    if (!firstName.trim()) local.firstName = 'required';
    if (!lastName.trim()) local.lastName = 'required';
    if (!email.trim()) local.email = 'required';
    if (!jobMatches) local.jobMatches = 'required';
    setErrors(local);
    setFormError(null);
    if (Object.keys(local).length) return;

    sending.current = true;
    setBusy(true);
    try {
      await submitSignup({
        firstName,
        lastName,
        email,
        linkedinUrl,
        cluster: cluster || 'other',
        via: params.get('via'),
        language,
        jobMatches,
        updates,
        website,
      });
      setDone(true);
    } catch (err) {
      if (err instanceof JoinError && err.status === 400) setErrors(err.details as FieldErrors);
      else if (err instanceof JoinError && err.code in copy.errors) setFormError(copy.errors[err.code as keyof typeof copy.errors]);
      else setFormError(copy.errors.failed);
    } finally {
      sending.current = false;
      setBusy(false);
    }
  }

  return (
    <JoinShell language={language} onLanguage={setLanguage} live={config.data?.live}>
      {done ? (
        <div>
          <h1 className="mb-2 text-2xl font-semibold text-gray-900">{copy.sentTitle}</h1>
          <p className="text-gray-700">{copy.sentBody}</p>
        </div>
      ) : (
        <form onSubmit={onSubmit} noValidate>
          <h1 className="mb-2 text-2xl font-semibold text-gray-900">{copy.title}</h1>
          {texts && <p className="mb-6 text-gray-700">{texts.intro}</p>}

          <div className="grid gap-4 sm:grid-cols-2">
            <Input label={copy.firstName} value={firstName} autoComplete="given-name" error={errorText(errors.firstName)}
              onChange={(e) => setFirstName((e.target as HTMLInputElement).value)} />
            <Input label={copy.lastName} value={lastName} autoComplete="family-name" error={errorText(errors.lastName)}
              onChange={(e) => setLastName((e.target as HTMLInputElement).value)} />
          </div>
          <div className="mt-4 grid gap-4">
            <Input label={copy.email} type="email" value={email} autoComplete="email" error={errorText(errors.email)}
              onChange={(e) => setEmail((e.target as HTMLInputElement).value)} />
            <Input label={copy.linkedin} type="url" value={linkedinUrl}
              error={errorText(errors.linkedinUrl)} onChange={(e) => setLinkedinUrl((e.target as HTMLInputElement).value)} />
            <label className="block text-sm text-gray-700">
              <span className="mb-1 block">{copy.cluster}</span>
              {/* Native <select> on purpose: this page is opened on phones from an InMail, and the
                  OS picker is the one people know there. Same documented deviation as the cockpit's. */}
              <select
                value={clusterKnown || !cluster ? cluster : ''}
                onChange={(e) => setCluster(e.target.value)}
                className="w-full rounded-sm border border-gray-300 bg-white px-3 py-3 text-base text-gray-900"
              >
                <option value="">—</option>
                {clusters.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c[language]}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {/* Honeypot: hidden from people and screen readers; only a bot fills it in. */}
          <input
            type="text"
            name="website"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            className="absolute left-[-9999px] h-0 w-0 opacity-0"
          />

          {texts && (
            <div className="mt-6 space-y-3 border-t border-gray-200 pt-6">
              <Checkbox label={texts.jobMatches} checked={jobMatches} error={Boolean(errors.jobMatches)}
                onCheckedChange={(v) => setJobMatches(v === true)} labelClassName="text-sm text-gray-800" />
              {errors.jobMatches && <p className="text-sm text-red-700">{errorText(errors.jobMatches, 'jobMatches')}</p>}
              <Checkbox label={texts.updates} checked={updates} onCheckedChange={(v) => setUpdates(v === true)}
                labelClassName="text-sm text-gray-800" />
              <p className="text-xs leading-relaxed text-gray-600">
                {texts.ai} {texts.rights} {texts.privacy}
              </p>
              <p className="text-xs leading-relaxed text-gray-600">{texts.nextStep}</p>
            </div>
          )}

          {formError && <p role="alert" className="mt-4 text-sm text-red-700">{formError}</p>}
          {config.isError && <p role="alert" className="mt-4 text-sm text-red-700">{copy.errors.network}</p>}

          <div className="mt-6">
            <Button type="submit" variant="primary" loading={busy} disabled={!texts}>
              {busy ? copy.sending : copy.submit}
            </Button>
          </div>
          {config.data && (
            <p className="mt-6 text-xs text-gray-400">
              {copy.draft}: {config.data.consentTextVersion}
            </p>
          )}
        </form>
      )}
    </JoinShell>
  );
}
